<script lang="ts">
    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import { focusNodeOnMount } from '@amp/web-app-components/src/actions/focus-node-on-mount';

    export let openModal: boolean;
    export let modalTriggerElement: HTMLElement | null;
    // rdar://101054693 (Lyrics : VO : Cannot access control buttons and lyrics in full Screen mode (Safari))
    export let useAriaModal: boolean = true;
    export let focusOnMount: boolean = true;
    export let closeOnClickOutside: boolean = true;

    const dispatch = createEventDispatcher();

    let domPortalElement: HTMLDivElement;
    let modalElement: HTMLElement;
    let contentWrapperElement: HTMLElement;

    export const handleModalClose = () => {
        openModal = false;
        document.body.classList.remove('modal-is-visible');
        modalTriggerElement?.focus();
        dispatch('close');
    };

    /**
     * ID for element that contains accessible modal title.
     */
    export let ariaLabelledBy: string | null = null;

    /**
     * Accessible modal title. Note that this should only be used when there is no element
     * containing the modal title that can be associated using `ariaLabelledBy`.
     */
    export let ariaLabel: string | null = null;

    /**
     * Support use ESC key to close modal
     * @param e {KeyboardEvent} keyboard  event
     *
     */
    function handleKeyup(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            handleModalClose();
        }
    }

    function handleBackdropClick() {
        if (closeOnClickOutside) {
            handleModalClose();
        }
    }

    onMount(() => {
        domPortalElement = document.createElement('div');
        domPortalElement.className = 'portal';
        document.body.appendChild(domPortalElement);
        domPortalElement.appendChild(modalElement);

        if (focusOnMount) {
            focusNodeOnMount(contentWrapperElement);
        }
    });

    onDestroy(() => {
        if (typeof document !== 'undefined') {
            document.body.removeChild(domPortalElement);
        }
    });
</script>

<!--
  @component
  Custom modal element wrapping a slot.
  This component is a centered modal with a backdrop
  On xs breakpoints, it becomes a full screen modal
 -->
<!--
    TODO: rdar://87280175 (MOTW: Shared component: Modal component is not correctly focus trapping)
    When focus trapping is implemented, this event should be on the modal
    element instead. This is a temporary workaround that binds the key event to
    `<body>` while the modal is mounted, so pressing Escape will close the
    modal even if focus has moved outside of it.
-->
<svelte:body on:keyup={handleKeyup} />

{#if openModal}
    <div data-testid="modal" class="modal" bind:this={modalElement}>
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
            class="backdrop"
            data-testid="modal-backdrop"
            on:click={handleBackdropClick}
        />
        <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
        <div
            data-testid="modal-content-wrapper"
            id="modal-content-wrapper"
            class="content-wrapper"
            role="dialog"
            tabindex="0"
            aria-modal={useAriaModal}
            aria-labelledby={ariaLabelledBy}
            aria-label={ariaLabel}
            bind:this={contentWrapperElement}
        >
            <slot />
        </div>
    </div>
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        opacity: 1;
        z-index: var(--z-modal);
    }

    .backdrop {
        background-color: var(--modalScrimColor);
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: calc(var(--z-modal) - 1);
    }

    .content-wrapper {
        width: var(--full-screen-modal-width, auto);
        height: var(--full-screen-modal-height, auto);
        max-width: var(--full-screen-modal-max-width, none);
        max-height: var(--full-screen-modal-max-height, none);
        position: relative;
        overflow: hidden;
        border-radius: var(--modalBorderRadius, $modal-border-radius);
        z-index: var(--z-modal);

        // rdar://122025336 (Remove references to lyrics after shared components are integrated)
        @supports #{'selector(:has(.lyrics__container))'} {
            &:global(:has(.lyrics__container)) {
                --full-screen-modal-width: 100%;
                --full-screen-modal-height: 100%;
                --full-screen-modal-max-width: #{$full-screen-lyrics-modal-max-width};
                --full-screen-modal-max-height: #{$full-screen-lyrics-modal-max-height};
                border-radius: 0;

                @media screen and ( min-height: #{$full-screen-lyrics-modal-max-height + 1px} ) and ( min-width: #{$full-screen-lyrics-modal-max-width + 1px} ) {
                    border-radius: #{$modal-border-radius};
                }
            }
        }

        // rdar://122025336 (Remove references to lyrics after shared components are integrated)
        @supports #{'selector(:has(.info__container))'} {
            &:global(:has(.info__container)) {
                --full-screen-modal-width: 100%;
                --full-screen-modal-height: 100%;
                --full-screen-modal-max-width: #{$full-screen-info-modal-max-width};
                --full-screen-modal-max-height: #{$full-screen-info-modal-max-height};
                border-radius: 0;

                @media screen and ( min-height: #{$full-screen-info-modal-max-height + 1px} ) and ( min-width: #{$full-screen-info-modal-max-width + 1px} ) {
                    border-radius: #{$modal-border-radius};
                }
            }
        }

        @supports #{'selector(:has(.desktop-now-playing__container))'} {
            &:global(:has(.desktop-now-playing__container)) {
                --full-screen-modal-width: 100%;
                --full-screen-modal-height: 100%;
                --full-screen-modal-max-width: #{$full-screen-player-modal-max-width};
                --full-screen-modal-max-height: #{$full-screen-player-modal-max-height};
                border-radius: 0;

                @media screen and ( min-height: #{$full-screen-player-modal-max-height + 1px} ) and ( min-width: #{$full-screen-player-modal-max-width + 1px} ) {
                    border-radius: #{$modal-border-radius};
                }
            }
        }
    }

    // go full screen on xsmall viewports and down
    @media (--range-xsmall-down) {
        .modal .content-wrapper {
            border-radius: 0;
            width: 100%;
            height: 100%;
        }
    }
</style>
